import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { ContractDetails } from '../IMessageBubbleInterface';
import { formatCurrency, getContractTypeBadge } from '../MessageBubbleHelpers';

import { BadgePill, CardFollowUp, CardHeaderRow, CardShell, MetricTile } from './sharedCardHelpers';

// Digit runs are capped at 15 (far beyond any real currency amount) so a pathologically long input
// can't force the regex engine to do unbounded work — matching fails fast within the bounded prefix
// instead of scanning the whole string, keeping this immune to super-linear runtime regardless of size.
const isNumericLikeValue = (value: string): boolean => /^\d{1,15}[.,]?\d{0,15}$/.test((value || '').trim());

export const ContractDetailsCard: React.FC<{ contractDetails: ContractDetails | null }> = ({ contractDetails }) => {
    if (!contractDetails) return null;
    return (
        <Box>
            <CardShell>
                <CardHeaderRow
                    contractId={contractDetails.contractId || 'Contract Details'}
                    right={
                        <Stack direction="row" alignItems="center" gap={0.6}>
                            <BadgePill>{getContractTypeBadge(contractDetails.type)}</BadgePill>
                            <BadgePill>{contractDetails.status || 'Active'}</BadgePill>
                        </Stack>
                    }
                />

                <Stack sx={{ p: 0.9, gap: 0.6 }}>
                    <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 600 }}>
                        {contractDetails.description || 'Contract details'}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            gap: 0.7,
                        }}
                    >
                        <MetricTile
                            label="MONTHLY PAYMENT"
                            value={
                                contractDetails.monthlyPayment ? formatCurrency(contractDetails.monthlyPayment) : '-'
                            }
                        />
                        <MetricTile
                            label="NEXT DUE"
                            value={
                                contractDetails.nextPaymentOn
                                    ? isNumericLikeValue(contractDetails.nextPaymentOn)
                                        ? formatCurrency(contractDetails.nextPaymentOn)
                                        : contractDetails.nextPaymentOn
                                    : '-'
                            }
                        />
                        <MetricTile
                            label="PRINCIPAL AMOUNT"
                            value={
                                contractDetails.principalAmount ? formatCurrency(contractDetails.principalAmount) : '-'
                            }
                        />
                        <MetricTile
                            label="LAST PAYMENT"
                            value={contractDetails.lastPayment ? formatCurrency(contractDetails.lastPayment) : '-'}
                        />
                        <MetricTile label="PROGRESS" value={contractDetails.progress || '-'} />
                    </Box>
                </Stack>
            </CardShell>

            <CardFollowUp text={contractDetails.followUp} />
        </Box>
    );
};
